export type ProtectedIntent = 'CreateProject' | 'AddProjectToCart' | 'ImageAdd' | 'GetFonts' | 'GetTheme';
export type AppProxyTokenTransport = 'direct' | 'parent';

interface ICachedAccessToken {
    token: string;
    expiresAt: number;
}

interface ITokenResponseMessage {
    type: 'printsuite:app-proxy-token-response';
    request_id: string;
    access_token?: unknown;
    expires_in?: unknown;
    error?: {
        code?: unknown;
        message?: unknown;
    };
}

interface IPendingRequest {
    resolve: (result: {token: string; expiresIn: number}) => void;
    reject: (error: Error) => void;
    timeoutId: ReturnType<typeof setTimeout>;
}

interface IMessageWindow {
    parent: {
        postMessage(message: unknown, targetOrigin: string): void;
    };
    addEventListener(type: 'message', listener: (event: MessageEvent) => void): void;
    removeEventListener(type: 'message', listener: (event: MessageEvent) => void): void;
}

export interface IAppProxyTokenProviderOptions {
    transport: AppProxyTokenTransport;
    authUrl?: string;
    parentOrigin?: string;
    sessionIdentifier: string;
    messageWindow?: IMessageWindow;
    fetchImplementation?: typeof fetch;
    requestTimeoutMs?: number;
}

const TOKEN_RESPONSE_TYPE = 'printsuite:app-proxy-token-response';
const TOKEN_REQUEST_TYPE = 'printsuite:app-proxy-token-request';
const DEFAULT_TIMEOUT_MS = 10000;
const MAX_PENDING_REQUESTS = 100;

export class AppProxyTokenProvider {
    private readonly options: IAppProxyTokenProviderOptions;
    private readonly accessTokens = new Map<ProtectedIntent, ICachedAccessToken>();
    private readonly tokenRequests = new Map<ProtectedIntent, Promise<string>>();
    private readonly pendingRequests = new Map<string, IPendingRequest>();
    private readonly messageWindow?: IMessageWindow;
    private readonly fetchImplementation: typeof fetch;
    private readonly requestTimeoutMs: number;
    private readonly handleMessage = (event: MessageEvent): void => this.onMessage(event);

    public constructor(options: IAppProxyTokenProviderOptions) {
        this.options = options;
        this.messageWindow = options.messageWindow || (typeof window === 'undefined' ? undefined : window);
        this.fetchImplementation = options.fetchImplementation || globalThis.fetch;
        this.requestTimeoutMs = options.requestTimeoutMs || DEFAULT_TIMEOUT_MS;

        if (options.transport === 'parent') {
            if (!options.parentOrigin || !this.messageWindow) {
                throw new Error('Missing or invalid parent token transport configuration');
            }
            this.messageWindow.addEventListener('message', this.handleMessage);
        }
    }

    public getAccessToken(intent: ProtectedIntent, forceRefresh: boolean = false): Promise<string> {
        if (forceRefresh) {
            this.accessTokens.delete(intent);
        } else {
            const cachedToken = this.accessTokens.get(intent);
            if (cachedToken && cachedToken.expiresAt > Date.now()) {
                return Promise.resolve(cachedToken.token);
            }
        }

        const pendingRequest = this.tokenRequests.get(intent);
        if (pendingRequest) {
            return pendingRequest;
        }

        const tokenRequest = this.requestToken(intent, forceRefresh).then(result => {
            this.accessTokens.set(intent, {
                token: result.token,
                expiresAt: Date.now() + Math.max(0, result.expiresIn - 10) * 1000
            });
            return result.token;
        }).finally(() => {
            this.tokenRequests.delete(intent);
        });

        this.tokenRequests.set(intent, tokenRequest);
        return tokenRequest;
    }

    public evict(intent: ProtectedIntent): void {
        this.accessTokens.delete(intent);
    }

    private requestToken(intent: ProtectedIntent, forceRefresh: boolean): Promise<{token: string; expiresIn: number}> {
        if (this.options.transport === 'parent') {
            return this.requestTokenFromParent(intent, forceRefresh);
        }
        return this.requestTokenDirectly(intent, forceRefresh);
    }

    private async requestTokenDirectly(intent: ProtectedIntent, forceRefresh: boolean):
        Promise<{token: string; expiresIn: number}> {
        if (!this.options.authUrl) {
            throw new Error('Missing or invalid App Proxy authorization URL');
        }
        const response = await this.fetchImplementation(this.options.authUrl, {
            method: 'POST',
            credentials: 'omit',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                session_identifier: this.options.sessionIdentifier,
                intent,
                force_refresh: forceRefresh
            })
        });
        if (!response.ok) {
            throw new Error('App Proxy authorization failed: ' + response.statusText);
        }

        const result = await response.json();
        const token = result?.access_token || result?.token;
        if (typeof token !== 'string' || !token) {
            throw new Error('App Proxy authorization response did not include an access token');
        }
        return {
            token,
            expiresIn: typeof result.expires_in === 'number' ? result.expires_in : 60
        };
    }

    private requestTokenFromParent(intent: ProtectedIntent, forceRefresh: boolean): Promise<{token: string; expiresIn: number}> {
        if (this.pendingRequests.size >= MAX_PENDING_REQUESTS) {
            return Promise.reject(new Error('Too many pending App Proxy authorization requests'));
        }

        const requestId = crypto.randomUUID();
        const request = new Promise<{token: string; expiresIn: number}>((resolve, reject) => {
            const timeoutId = setTimeout(() => {
                if (this.pendingRequests.delete(requestId)) {
                    reject(new Error('App Proxy authorization request timed out'));
                }
            }, this.requestTimeoutMs);
            this.pendingRequests.set(requestId, {resolve, reject, timeoutId});
        });

        this.messageWindow.parent.postMessage({
            type: TOKEN_REQUEST_TYPE,
            request_id: requestId,
            session_identifier: this.options.sessionIdentifier,
            intent,
            force_refresh: forceRefresh
        }, this.options.parentOrigin);
        return request;
    }

    private onMessage(event: MessageEvent): void {
        if (event.origin !== this.options.parentOrigin || event.source !== this.messageWindow.parent) {
            return;
        }
        const data = event.data as ITokenResponseMessage;
        if (!data || data.type !== TOKEN_RESPONSE_TYPE || typeof data.request_id !== 'string') {
            return;
        }

        const pending = this.pendingRequests.get(data.request_id);
        if (!pending) {
            return;
        }
        this.pendingRequests.delete(data.request_id);
        clearTimeout(pending.timeoutId);

        if (data.error) {
            const message = typeof data.error.message === 'string' && data.error.message
                ? data.error.message
                : 'App Proxy authorization failed';
            pending.reject(new Error(message));
            return;
        }
        if (typeof data.access_token !== 'string' || !data.access_token) {
            pending.reject(new Error('App Proxy authorization response did not include an access token'));
            return;
        }
        pending.resolve({
            token: data.access_token,
            expiresIn: typeof data.expires_in === 'number' ? data.expires_in : 60
        });
    }
}

export async function postWithCapabilityToken(
    tokenProvider: AppProxyTokenProvider,
    intent: ProtectedIntent,
    post: (token: string) => Promise<Response>
): Promise<Response> {
    let response = await post(await tokenProvider.getAccessToken(intent));
    if (response.status === 401) {
        tokenProvider.evict(intent);
        response = await post(await tokenProvider.getAccessToken(intent, true));
    }
    return response;
}
